import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react';
import clsx from 'clsx';

const SearchableSelect = ({ options, value, onChange, placeholder = "請選擇", className, prefix, wrap = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    const filteredOptions = options.filter(option =>
        option.name != null && option.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const isOverflow = wrap;

    const handleSelect = (option) => {
        onChange(option);
        setIsOpen(false);
        setSearchTerm("");
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange(null);
    };

    return (
        <div className={clsx("relative w-full", className)} ref={dropdownRef}>
            <div
                className="w-full min-h-[50px] bg-white rounded-[24px] flex items-center py-2 px-4 cursor-pointer outline outline-1 outline-[rgba(23,23,23,0.10)]"
                onClick={() => {
                    setIsOpen(!isOpen);
                    if (!isOpen) {
                        setTimeout(() => inputRef.current?.focus(), 100);
                    }
                }}
            >
                <div className="flex-1 flex items-center justify-between overflow-hidden">
                    <div className={clsx("flex-1 min-w-0", isOverflow ? "flex flex-col" : "flex items-center")}>
                        {prefix && <div className="text-[rgba(110,98,91,0.50)] text-[18px] font-light whitespace-nowrap shrink-0 flex items-center" style={{ fontFamily: 'Noto Sans TC' }}>{prefix}</div>}
                        {isOpen ? (
                            <input
                                ref={inputRef}
                                type="text"
                                className="w-full bg-transparent outline-none text-[var(--color-text-main)] text-[18px] font-light placeholder:text-[rgba(110,98,91,0.30)] min-w-0"
                                style={{ fontFamily: 'Noto Sans TC' }}
                                placeholder={value ? value.name : placeholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        ) : value ? (
                            <span
                                className={clsx(
                                    "text-[var(--color-text-main)] text-[18px] font-light",
                                    isOverflow ? "break-words" : "truncate"
                                )}
                                style={{ fontFamily: 'Noto Sans TC' }}
                            >
                                {value.name}
                            </span>
                        ) : (
                            <span className={clsx("text-[rgba(110,98,91,0.30)] text-[18px] font-light", wrap ? "break-words" : "truncate")} style={{ fontFamily: 'Noto Sans TC' }}>{placeholder}</span>
                        )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0 self-center">
                        {value && (
                            <div
                                onClick={handleClear}
                                className="p-1 hover:bg-stone-100 rounded-full cursor-pointer"
                            >
                                <X size={16} className="text-[rgba(110,98,91,0.40)]" />
                            </div>
                        )}
                        <ChevronDown size={20} className={clsx("text-[rgba(110,98,91,0.40)] transition-transform", isOpen && "rotate-180")} />
                    </div>
                </div>
            </div>

            {isOpen && (
                <div className="absolute top-[calc(100%+8px)] left-0 w-full bg-[var(--color-surface)] rounded-[13px] shadow-xl z-50 overflow-hidden border border-stone-100 max-h-[300px] flex flex-col">

                    <div className="overflow-y-auto flex-1 p-1">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <div
                                    key={option.id}
                                    className={clsx(
                                        "px-4 py-3 cursor-pointer rounded-lg flex items-center justify-between hover:bg-[var(--color-background)] transition-colors",
                                        value?.id === option.id && "bg-[var(--color-background)] text-[var(--color-primary)] font-medium"
                                    )}
                                    onClick={() => handleSelect(option)}
                                >
                                    <span>{option.name}</span>
                                    {value?.id === option.id && <Check size={18} />}
                                </div>
                            ))
                        ) : (
                            <div className="px-4 py-8 text-center text-stone-400 text-sm">
                                查無結果
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
